import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FaUtensils, FaSignOutAlt, FaClipboardList, FaListAlt, FaChartBar, FaQrcode, FaChartLine } from 'react-icons/fa';
import { QRCodeCanvas } from 'qrcode.react';
import { restaurantAPI, authAPI, getRestaurantId, waiterAPI } from '../utils/api';
import { buildCircledLogoDataUrl } from '../utils/qrLogo';
import { useAdminRealtimeData } from '../context/AdminRealtimeDataContext';
import './AdminDashboard.css';
import FullPageLoader from '../components/FullPageLoader';

const DEFAULT_QR_LOGO_DATA_URL = (() => {
  // Prefer the real brand logo from CRA public folder.
  // This assumes `public/logo.png` exists in the deployed Admin app.
  const publicLogo = `${process.env.PUBLIC_URL || ''}/logo.png`;

  // Fallback: lightweight embedded SVG so QR still renders if the PNG isn't present.
  const svg = `
    <svg xmlns="http://www.w3.org/2000/svg" width="128" height="128" viewBox="0 0 128 128">
      <defs>
        <linearGradient id="g" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stop-color="#111827"/>
          <stop offset="1" stop-color="#374151"/>
        </linearGradient>
      </defs>
      <circle cx="64" cy="64" r="60" fill="url(#g)"/>
      <circle cx="64" cy="64" r="58" fill="none" stroke="#ffffff" stroke-width="4" opacity="0.9"/>
      <text x="64" y="74" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="44" font-weight="700" fill="#ffffff">MB</text>
    </svg>
  `.trim();

  return publicLogo || `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
})();

function AdminDashboard() {
  const navigate = useNavigate();
  const {
    restaurant,
    setRestaurant,
    restaurantForm,
    setRestaurantForm,
    stats,
    waiterCalls,
    initialLoading,
    refreshWaiterCalls,
  } = useAdminRealtimeData();

  // Local-only UI state
  const [loading, setLoading] = useState(false);
  const [showWaiterAlert, setShowWaiterAlert] = useState(false);
  const [isEditingRestaurant, setIsEditingRestaurant] = useState(false);
  const [wifiPairStatus, setWifiPairStatus] = useState({ type: '', message: '' });
  const [isPairingWifi, setIsPairingWifi] = useState(false);
  const [restaurantSaveState, setRestaurantSaveState] = useState({ loading: false, error: '', success: '' });
  const qrCanvasRef = useRef(null);
  const [circledQrLogoSrc, setCircledQrLogoSrc] = useState(null);

  const handleLogout = () => {
    authAPI.logout();
    navigate('/login');
  };

  const startEditRestaurant = () => {
    setRestaurantSaveState({ loading: false, error: '', success: '' });
    setRestaurantForm({
      name: restaurant?.name || '',
      phone: restaurant?.phone || '',
      address: restaurant?.address || '',
      location: restaurant?.location || '',
      numberOfTables: restaurant?.number_of_tables ?? restaurant?.numberOfTables ?? '',
      wifiOnlyEnabled: !!restaurant?.wifi_only_enabled,
      wifiAllowedCidrs: restaurant?.wifi_allowed_cidrs || '',
      gstNumber: restaurant?.gst_number || '',
      gstRatePercent: restaurant?.gst_rate_percent ?? '',
    });
    setIsEditingRestaurant(true);
  };

  const cancelEditRestaurant = () => {
    setRestaurantSaveState({ loading: false, error: '', success: '' });
    setIsEditingRestaurant(false);
    setRestaurantForm({
      name: restaurant?.name || '',
      phone: restaurant?.phone || '',
      address: restaurant?.address || '',
      location: restaurant?.location || '',
      numberOfTables: restaurant?.number_of_tables ?? restaurant?.numberOfTables ?? '',
      wifiOnlyEnabled: !!restaurant?.wifi_only_enabled,
      wifiAllowedCidrs: restaurant?.wifi_allowed_cidrs || '',
      gstNumber: restaurant?.gst_number || '',
      gstRatePercent: restaurant?.gst_rate_percent ?? '',
    });
  };

  const handlePairWifi = async () => {
    setWifiPairStatus({ type: '', message: '' });
    setIsPairingWifi(true);
    try {
      const res = await restaurantAPI.pairWifi();
      const updated = res?.data?.restaurant;
      const pairedCidr = res?.data?.pairedCidr || updated?.wifi_allowed_cidrs || '';

      if (updated) {
        setRestaurant(updated);
      }

      setRestaurantForm((p) => ({
        ...p,
        wifiOnlyEnabled: true,
        wifiAllowedCidrs: pairedCidr,
      }));

      setWifiPairStatus({
        type: 'success',
        message: `Paired successfully. Allowed CIDR set to: ${pairedCidr}`,
      });
    } catch (e) {
      setWifiPairStatus({
        type: 'error',
        message: e?.message || 'Failed to pair WiFi',
      });
    } finally {
      setIsPairingWifi(false);
    }
  };

  const saveRestaurantInfo = async () => {
    try {
      setRestaurantSaveState({ loading: true, error: '', success: '' });

      const payload = {
        name: String(restaurantForm.name || '').trim(),
        phone: String(restaurantForm.phone || '').trim(),
        address: String(restaurantForm.address || '').trim(),
        location: String(restaurantForm.location || '').trim(),
        numberOfTables: restaurantForm.numberOfTables === '' ? undefined : Number(restaurantForm.numberOfTables),
        wifiOnlyEnabled: !!restaurantForm.wifiOnlyEnabled,
        wifiAllowedCidrs: String(restaurantForm.wifiAllowedCidrs || '').trim(),
        gstNumber: String(restaurantForm.gstNumber || '').trim(),
        gstRatePercent:
          restaurantForm.gstRatePercent === '' || restaurantForm.gstRatePercent == null
            ? null
            : Number(restaurantForm.gstRatePercent),
        upiId: String(restaurantForm.upiId || '').trim(),
      };

      const res = await restaurantAPI.update(payload);
      const updated = res?.data?.restaurant;
      if (updated) {
        setRestaurant(updated);
        setRestaurantForm({
          name: updated?.name || '',
          phone: updated?.phone || '',
          address: updated?.address || '',
          location: updated?.location || '',
          numberOfTables: updated?.number_of_tables ?? updated?.numberOfTables ?? '',
          wifiOnlyEnabled: !!updated?.wifi_only_enabled,
          wifiAllowedCidrs: updated?.wifi_allowed_cidrs || '',
          gstNumber: updated?.gst_number || '',
          gstRatePercent: updated?.gst_rate_percent ?? '',
          upiId: updated?.upi_id || '',
        });
      }

      setRestaurantSaveState({ loading: false, error: '', success: 'Restaurant info updated.' });
      setIsEditingRestaurant(false);
    } catch (e) {
      setRestaurantSaveState({ loading: false, error: e.message || 'Failed to update restaurant.', success: '' });
    }
  };

  const fetchWaiterCalls = async (showIfAny) => {
    try {
      const calls = await refreshWaiterCalls();
      if (showIfAny && calls.length > 0) {
        setShowWaiterAlert(true);
      }
    } catch (error) {
      console.error('Error fetching waiter calls:', error);
    }
  };

  const acknowledgeCall = async (id) => {
    try {
      await waiterAPI.acknowledge(id);
      const updatedCalls = await refreshWaiterCalls();
      if (!updatedCalls.length) {
        setShowWaiterAlert(false);
      }
    } catch (error) {
      console.error('Error acknowledging waiter call:', error);
    }
  };

  useEffect(() => {
    let alive = true;

    const run = async () => {
      const dataUrl = await buildCircledLogoDataUrl({
        logoSrc: DEFAULT_QR_LOGO_DATA_URL,
        sizePx: 256,
        borderColor: '#f97316',
        borderWidthPx: 18,
        paddingPx: 18,
      });
      if (alive) setCircledQrLogoSrc(dataUrl);
    };

    run();

    return () => {
      alive = false;
    };
  }, []);

  // Auto-open waiter calls panel whenever new calls arrive.
  // Previously, the UI required `showWaiterAlert=true` but nothing was setting it,
  // so calls existed in state but were never shown.
  useEffect(() => {
    if (waiterCalls.length > 0) {
      setShowWaiterAlert(true);
    }
  }, [waiterCalls]);

  // Show full-page loading ONLY when app is freshly loaded (manual refresh / first mount)
  // Once restaurant is cached in context, navigating between pages should never show this.
  if (initialLoading || !restaurant) {
    return <FullPageLoader message="Loading dashboard…" />;
  }

  // Customer-facing app URL used for QR codes / sharing
  const publicAppBase = (process.env.REACT_APP_PUBLIC_APP_URL || 'https://app.menubazzar.com').replace(/\/+$/, '');
  const menuUrl = `${publicAppBase}/customer/menu?restaurantId=${restaurant.id}`;

  const downloadQr = (format = 'png') => {
    const canvas = qrCanvasRef.current;
    if (!canvas) return;

    let dataUrl;

    if (format === 'png') {
      dataUrl = canvas.toDataURL('image/png');
    } else {
      // convert to JPEG via an offscreen canvas
      const tmpCanvas = document.createElement('canvas');
      tmpCanvas.width = canvas.width;
      tmpCanvas.height = canvas.height;
      const ctx = tmpCanvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, tmpCanvas.width, tmpCanvas.height);
      ctx.drawImage(canvas, 0, 0);
      dataUrl = tmpCanvas.toDataURL('image/jpeg', 0.95);
    }

    const link = document.createElement('a');
    link.href = dataUrl;
    link.download = `MenuBazzar-qr-${restaurant.id}.${format}`;
    link.click();
  };

  return (
    <div className="admin-dashboard">
      <header className="dashboard-header">
        <div className="header-content">
          <div className="header-left">
            <FaUtensils className="header-icon" />
            <div>
              <h1 className="dashboard-title">{restaurant.name}</h1>
              <p className="restaurant-subtitle">Admin Dashboard</p>
            </div>
          </div>
          <button className="logout-btn" onClick={handleLogout}>
            <FaSignOutAlt /> Logout
          </button>
        </div>
      </header>

      <div className="dashboard-content">
        {waiterCalls.length > 0 && showWaiterAlert && (
          <div className="waiter-calls-panel">
            <div className="waiter-calls-header">
              <div>
                <strong>Customer is Calling</strong>
                <p>These tables have requested a waiter.</p>
              </div>
              <button
                type="button"
                className="waiter-calls-hide-btn"
                onClick={() => setShowWaiterAlert(false)}
              >
                Hide
              </button>
            </div>
            <div className="waiter-calls-list">
              {waiterCalls.map(call => (
                <div key={call.id} className="waiter-call-item">
                  <div className="waiter-call-info">
                    <span className="waiter-call-table">
                      Table {call.table_number || 'Unknown'}
                    </span>
                    {call.created_at && (
                      <span className="waiter-call-time">
                        {new Date(call.created_at).toLocaleTimeString()}
                      </span>
                    )}
                  </div>
                  <button
                    type="button"
                    className="waiter-alert-btn primary"
                    onClick={() => acknowledgeCall(call.id)}
                  >
                    Mark as attended
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
        <div className="stats-grid">
          <div className="stat-card">
            <div className="stat-icon menu">
              <FaListAlt />
            </div>
            <div className="stat-info">
              <h3 className="stat-value">{stats.totalMenuItems}</h3>
              <p className="stat-label">Menu Items</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon orders">
              <FaClipboardList />
            </div>
            <div className="stat-info">
              <h3 className="stat-value">{stats.totalOrders}</h3>
              <p className="stat-label">Total Orders</p>
            </div>
          </div>

          <div className="stat-card">
            <div className="stat-icon pending">
              <FaChartBar />
            </div>
            <div className="stat-info">
              <h3 className="stat-value">{stats.pendingOrders}</h3>
              <p className="stat-label">Pending Orders</p>
            </div>
          </div>
        </div>

        <div className="dashboard-actions">
        <Link to="/sales" className="action-card">
          <div className="action-icon analytics">
            <FaChartLine />
          </div>
          <h3>View Sales</h3>
          <p>Daily, weekly & monthly analytics</p>
        </Link>
          <Link to="/menu" className="action-card">
            <div className="action-icon menu">
              <FaListAlt />
            </div>
            <h3>Manage Menu</h3>
            <p>Add, edit, or remove menu items</p>
          </Link>

          <Link to="/orders" className="action-card">
            <div className="action-icon orders">
              <FaClipboardList />
            </div>
            <h3>View Orders</h3>
            <p>Manage and track customer orders</p>
          </Link>

          <Link to="/bills" className="action-card">
            <div className="action-icon orders">
              <FaClipboardList />
            </div>
            <h3>View Bills</h3>
            <p>Review and export customer bills</p>
          </Link>
        </div>

        {restaurant.id && (
          <div className="qr-code-section">
            <h3>
              <FaQrcode /> Customer Menu QR Code
            </h3>
                <p className="qr-description">Scan this QR code to access your restaurant's digital menu</p>
            <div className="qr-container">
              <div className="qr-code-wrapper">
                <QRCodeCanvas
                  value={menuUrl}
                  size={200}
                  includeMargin={true}
                  level="H"
                  imageSettings={{
                    src: circledQrLogoSrc || DEFAULT_QR_LOGO_DATA_URL,
                    height: 52,
                    width: 52,
                    excavate: true,
                  }}
                  ref={qrCanvasRef}
                />
              </div>
              <div className="qr-info">
                <p className="qr-url">
                  <strong>Menu URL:</strong>
                  <br />
                  <a 
                    href={menuUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="qr-link"
                  >
                    {menuUrl}
                  </a>
                </p>
                <div className="qr-download-actions">
                  <button
                    type="button"
                    className="qr-download-btn primary"
                    onClick={() => downloadQr('png')}
                  >
                    Download PNG
                  </button>
                  <button
                    type="button"
                    className="qr-download-btn"
                    onClick={() => downloadQr('jpg')}
                  >
                    Download JPG
                  </button>
                </div>
                <p className="qr-instructions">
                  Print this QR code and place it on tables so customers can scan and view your menu instantly.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="restaurant-info">
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
            <h3>Restaurant Information</h3>
            {!isEditingRestaurant ? (
              <button type="button" className="qr-download-btn" onClick={startEditRestaurant}>
                Edit
              </button>
            ) : (
              <div style={{ display: 'flex', gap: 10 }}>
                <button
                  type="button"
                  className="qr-download-btn primary"
                  onClick={saveRestaurantInfo}
                  disabled={restaurantSaveState.loading}
                >
                  {restaurantSaveState.loading ? 'Saving...' : 'Save'}
                </button>
                <button type="button" className="qr-download-btn" onClick={cancelEditRestaurant} disabled={restaurantSaveState.loading}>
                  Cancel
                </button>
              </div>
            )}
          </div>

          {restaurantSaveState.error && (
            <div style={{ marginTop: 12, color: '#991b1b', background: '#fee2e2', border: '1px solid #fecaca', padding: 10, borderRadius: 10 }}>
              {restaurantSaveState.error}
            </div>
          )}
          {restaurantSaveState.success && (
            <div style={{ marginTop: 12, color: '#065f46', background: '#d1fae5', border: '1px solid #a7f3d0', padding: 10, borderRadius: 10 }}>
              {restaurantSaveState.success}
            </div>
          )}

          {!isEditingRestaurant ? (
            <div className="info-grid">
              <div className="info-item">
                <strong>Email:</strong> {restaurant.email || 'Not set'}
              </div>
              <div className="info-item">
                <strong>Phone:</strong> {restaurant.phone || 'Not set'}
              </div>
              <div className="info-item">
                <strong>Location:</strong> {restaurant.location || 'Not set'}
              </div>
              <div className="info-item">
                <strong>Tables:</strong> {restaurant.number_of_tables ?? restaurant.numberOfTables ?? 'Not set'}
              </div>
              <div className="info-item">
                <strong>GST:</strong> {restaurant.gst_number || 'Not set'}
              </div>
              <div className="info-item">
                <strong>GST Rate:</strong>{' '}
                {restaurant.gst_rate_percent == null || restaurant.gst_rate_percent === ''
                  ? 'Not set'
                  : `${restaurant.gst_rate_percent}%`}
              </div>
              <div className="info-item">
                <strong>UPI ID:</strong> {restaurant.upi_id || 'Not set'}
              </div>
              <div className="info-item full-width">
                <strong>Address:</strong> {restaurant.address || 'Not set'}
              </div>
            </div>
          ) : (
            <div className="info-grid">
              <div className="info-item">
                <strong>Name:</strong>
                <input
                  value={restaurantForm.name}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, name: e.target.value }))}
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div className="info-item">
                <strong>Phone:</strong>
                <input
                  value={restaurantForm.phone}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, phone: e.target.value }))}
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div className="info-item">
                <strong>Location:</strong>
                <input
                  value={restaurantForm.location}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, location: e.target.value }))}
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div className="info-item">
                <strong>Tables:</strong>
                <input
                  type="number"
                  min={1}
                  max={500}
                  value={restaurantForm.numberOfTables}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, numberOfTables: e.target.value }))}
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb' }}
                />
              </div>
              <div className="info-item full-width">
                <strong>Wi‑Fi Only Mode:</strong>
                <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <input
                    type="checkbox"
                    checked={!!restaurantForm.wifiOnlyEnabled}
                    onChange={(e) => setRestaurantForm((p) => ({ ...p, wifiOnlyEnabled: e.target.checked }))}
                  />
                  <span style={{ color: '#6b7280' }}>
                    When enabled, customers can access menu/order only from your restaurant Wi‑Fi.
                  </span>
                </div>
              </div>

              <div className="info-item full-width">
                <strong>Allowed Wi‑Fi IP ranges (CIDR):</strong>
                <textarea
                  rows={2}
                  value={restaurantForm.wifiAllowedCidrs}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, wifiAllowedCidrs: e.target.value }))}
                  placeholder="Example: 192.168.0.0/24,10.0.0.0/8"
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb', resize: 'vertical' }}
                />
                <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                  Tip: Click Pair current WiFi while the admin device is connected to restaurant WiFi.
                </div>

                <div style={{ marginTop: 10, display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={handlePairWifi}
                    disabled={isPairingWifi}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1px solid #111827',
                      background: '#111827',
                      color: '#fff',
                      fontWeight: 700,
                      cursor: isPairingWifi ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {isPairingWifi ? 'Pairing…' : 'Pair current Wi‑Fi'}
                  </button>

                  <button
                    type="button"
                    onClick={() => {
                      setRestaurantForm((p) => ({ ...p, wifiOnlyEnabled: false }));
                      setWifiPairStatus({ type: 'info', message: 'Wi‑Fi only disabled (remember to Save Changes).' });
                    }}
                    style={{
                      padding: '10px 14px',
                      borderRadius: 10,
                      border: '1px solid #e5e7eb',
                      background: '#f3f4f6',
                      color: '#111827',
                      fontWeight: 700,
                      cursor: 'pointer'
                    }}
                  >
                    Disable Wi‑Fi only
                  </button>
                </div>

                {wifiPairStatus.message ? (
                  <div
                    style={{
                      marginTop: 10,
                      padding: '10px 12px',
                      borderRadius: 10,
                      background:
                        wifiPairStatus.type === 'success'
                          ? '#ecfdf5'
                          : wifiPairStatus.type === 'error'
                            ? '#fef2f2'
                            : '#eff6ff',
                      color:
                        wifiPairStatus.type === 'success'
                          ? '#065f46'
                          : wifiPairStatus.type === 'error'
                            ? '#991b1b'
                            : '#1e3a8a',
                      border:
                        wifiPairStatus.type === 'success'
                          ? '1px solid #a7f3d0'
                          : wifiPairStatus.type === 'error'
                            ? '1px solid #fecaca'
                            : '1px solid #bfdbfe',
                      fontSize: 13
                    }}
                  >
                    {wifiPairStatus.message}
                  </div>
                ) : null}
              </div>

              <div className="info-item">
                <strong>GST Number:</strong>
                <input
                  value={restaurantForm.gstNumber}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, gstNumber: e.target.value }))}
                  placeholder="Example: 22AAAAA0000A1Z5"
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb' }}
                />
              </div>

              <div className="info-item">
                <strong>GST Rate (%):</strong>
                <input
                  type="number"
                  min={0}
                  max={28}
                  step={0.01}
                  value={restaurantForm.gstRatePercent}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, gstRatePercent: e.target.value }))}
                  placeholder="Example: 5"
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb' }}
                />
              </div>

              <div className="info-item">
                <strong>UPI ID:</strong>
                <input
                  value={restaurantForm.upiId}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, upiId: e.target.value }))}
                  placeholder="Example: restaurant@upi"
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb' }}
                />
                <div style={{ marginTop: 6, color: '#6b7280', fontSize: 12 }}>
                  UPI ID for payment QR code on bills. Customers can scan and pay directly.
                </div>
              </div>

              <div className="info-item full-width">
                <strong>Address:</strong>
                <textarea
                  rows={3}
                  value={restaurantForm.address}
                  onChange={(e) => setRestaurantForm((p) => ({ ...p, address: e.target.value }))}
                  style={{ width: '100%', marginTop: 6, padding: 10, borderRadius: 10, border: '1px solid #e5e7eb', resize: 'vertical' }}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default AdminDashboard;


